import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import { ContentModal, ButtonNew, InputTitle, TextInput } from '@hems/component';
import { EnergyManagementService } from '@hems/service';
import { useToast } from '@hems/util';
import { BUTTON_NEW_TYPE } from '@hems/util/src/constant';
import { NAME_INPUT_DEBOUNCE_WAIT_TIME, VALIDATE_SCHEDULE_PRESET_NAME } from '@hems/util/src/constant/energyManagement';
import { defaultInputErrorInfo } from '@hems/util/src/helper/helper';
const PRESET_NAME_MAX_LENGTH = 25;
export default defineComponent({
    name: 'PresetNameSettingModal',
    components: {
        ContentModal,
        InputTitle,
        TextInput,
        ButtonNew,
    },
    props: {
        currentPresetName: {
            type: String,
            default: '',
        },
        siteId: {
            type: [Number, null],
            required: true,
        },
    },
    emits: ['confirm', 'delete'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const energyManagementService = new EnergyManagementService(window.axiosInstance.axios);
        const { openToastMessage } = useToast();
        const presetName = ref(props.currentPresetName);
        const presetNameErrorInfo = ref({ isError: false, errorMessage: '' });
        const isValidPresetName = ref(false);
        const isSaveButtonDisabled = computed(() => !presetName.value ||
            !isValidPresetName.value ||
            presetNameErrorInfo.value.isError ||
            presetName.value === props.currentPresetName);
        const isNew = computed(() => !props.currentPresetName);
        const title = computed(() => (isNew.value ? t('control.add_preset') : t('common.edit_info')));
        const checkIsDuplicate = async (targetPresetName) => {
            try {
                if (isNil(props.siteId) || targetPresetName === props.currentPresetName) {
                    return;
                }
                const validateResult = await energyManagementService.validatePresetName(props.siteId, {
                    value: targetPresetName,
                });
                if (presetName.value !== targetPresetName)
                    return;
                const isDuplicate = validateResult === VALIDATE_SCHEDULE_PRESET_NAME.DUPLICATE;
                if (!isDuplicate) {
                    presetNameErrorInfo.value = { ...defaultInputErrorInfo };
                    isValidPresetName.value = true;
                    return;
                }
                presetNameErrorInfo.value = {
                    isError: true,
                    errorMessage: t('message.name_already_exists'),
                };
            }
            catch (e) {
                console.error(e);
                openToastMessage(t('message.unable_connect_server'));
                return;
            }
        };
        const debouncedCheckIsDuplicate = debounce(checkIsDuplicate, NAME_INPUT_DEBOUNCE_WAIT_TIME);
        const checkIsValidate = async (presetName) => {
            isValidPresetName.value = false;
            if (presetName.length > PRESET_NAME_MAX_LENGTH) {
                presetNameErrorInfo.value = {
                    isError: true,
                    errorMessage: t('message.maximum_characters_length', { length: PRESET_NAME_MAX_LENGTH }),
                };
                return;
            }
            debouncedCheckIsDuplicate(presetName);
        };
        const onSave = () => {
            emit('confirm', presetName.value);
        };
        return {
            presetName,
            isSaveButtonDisabled,
            presetNameErrorInfo,
            PRESET_NAME_MAX_LENGTH,
            title,
            isNew,
            BUTTON_NEW_TYPE,
            checkIsValidate,
            onSave,
        };
    },
});
